import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { getAdressTypeOption } from "api";
import usePermissions from "hooks/usePermissions";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { handleRenderAddressOption } from "helpers/schedulehelper";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import testIds from "constants/testIds";
import SearchSelect from "../../Common/SearchSelect";
import { IAddressOption, IPortLinkedAddress } from "../Types";

interface PortFormProps {
  isPickupOrDelivery: string;
  formState: RequestFormState;
  portAddressOption: any[];
  getTimeZones: Function;
  setSearch: Dispatch<SetStateAction<string>>;
  /** Store IANA tz from the port API linked `address` so the parent can restore it if the optional book address is cleared. */
  registerPortLinkedTimezone?: (timezone: string | null) => void;
}

export default function PortForm({
  isPickupOrDelivery,
  formState,
  portAddressOption,
  getTimeZones,
  setSearch,
  registerPortLinkedTimezone,
}: PortFormProps) {
  const intl = useIntl();
  const { permissions }: any = usePermissions();
  const { enable_new_address_structure } = JSON.parse(permissions)?.company_shipper_settings;
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  const { getValues, setValue, control } = useFormContext();
  const clearSelectedAddress = useCallback(() => {
    const codeType = isPickupOrDelivery === "stop1" ? "destination" : isPickupOrDelivery;
    setValue(`schedule.${isPickupOrDelivery}_address_name`, "", { shouldValidate: true });
    setValue(`schedule.${isPickupOrDelivery}_street`, "", { shouldValidate: true });
    setValue(`schedule.${isPickupOrDelivery}_street_line2`, "");
    setValue(`schedule.${isPickupOrDelivery}_city`, "", { shouldValidate: true });
    setValue(`schedule.${isPickupOrDelivery}_state`, "");
    setValue(`schedule.${isPickupOrDelivery}_country`, "", { shouldValidate: true });
    setValue(`schedule.${isPickupOrDelivery}_country_code`, "");
    setValue(`schedule.${isPickupOrDelivery}_zipcode`, "", { shouldValidate: true });
    setValue(`schedule.${isPickupOrDelivery}_un_locode`, "");
    setValue(`schedule.${isPickupOrDelivery}_location_reference_number`, "");
    setValue(`schedule.${isPickupOrDelivery}_custom_address_reference`, "");
    setValue(`schedule.${codeType}_address_reference_number`, "");
    setValue(`schedule.${codeType}_address_pk`, undefined);
  }, [isPickupOrDelivery, setValue]);
  const clearSelectedPort = useCallback(() => {
    setValue(`schedule.${isPickupOrDelivery}_airport`, "");
    setValue(`schedule.${isPickupOrDelivery}_airport_code`, "");
    setValue(`schedule.${isPickupOrDelivery}_seaport`, "");
    setValue(`schedule.${isPickupOrDelivery}_seaport_code`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_city`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_country`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_country_code`, "");
    setValue(`schedule.${isPickupOrDelivery}_timezone`, "");
    registerPortLinkedTimezone?.(null);
  }, [isPickupOrDelivery, registerPortLinkedTimezone, setValue]);

  const handleFillPortAddressOption = useCallback(
    (event, options: { preserveSelectedAddress?: boolean } = {}) => {
      const address = event[0];
      if (!address) return;
      if (!options.preserveSelectedAddress) {
        clearSelectedAddress();
      }
      const linked = address?.address as IPortLinkedAddress | undefined;
      const linkedAddressTimezone = linked?.timezone || "";
      const preferredTimezone = linkedAddressTimezone || getValues(`schedule.${isPickupOrDelivery}_timezone`);

      if (isOptionalPortAddressFormEnabled && linkedAddressTimezone) {
        setValue(`schedule.${isPickupOrDelivery}_timezone`, linkedAddressTimezone, { shouldValidate: true });
      }

      if (getValues(`schedule.${isPickupOrDelivery}_address_type`) === "seaport") {
        getTimeZones(true, address?.country_code, isOptionalPortAddressFormEnabled ? preferredTimezone : "");
        setValue(`schedule.${isPickupOrDelivery}_seaport`, address?.city_name);
        setValue(`schedule.${isPickupOrDelivery}_seaport_code`, address?.code);
      } else {
        getTimeZones(true, address?.country_code, isOptionalPortAddressFormEnabled ? preferredTimezone : "");
        setValue(`schedule.${isPickupOrDelivery}_airport`, address?.city_name);
        setValue(`schedule.${isPickupOrDelivery}_airport_code`, address?.code);
      }

      if (address.city_name) {
        setValue(`schedule.${isPickupOrDelivery}_port_city`, address.city_name, { shouldValidate: true });
      }
      if (address.country_name) {
        setValue(`schedule.${isPickupOrDelivery}_port_country`, address.country_name, { shouldValidate: true });
      }
      if (address.country_code) {
        setValue(`schedule.${isPickupOrDelivery}_port_country_code`, address.country_code, { shouldValidate: true });
      }

      if (isOptionalPortAddressFormEnabled) {
        registerPortLinkedTimezone?.(linked?.timezone || null);
      }
    },
    [
      clearSelectedAddress,
      getTimeZones,
      getValues,
      isPickupOrDelivery,
      isOptionalPortAddressFormEnabled,
      registerPortLinkedTimezone,
      setValue,
    ],
  );

  // On initial mount of an edited / duplicated draft, the airport/seaport
  // code+name fields are prefilled but the SearchSelect treats them as a
  // free-string. Re-resolve the prefilled code (or name) against the API and
  // simulate a selection to populate the linked address.
  //
  // NOTE: the form is hydrated asynchronously via methods.reset(...) AFTER
  // this component mounts. We MUST react to the prefilled fields appearing,
  // so we read them via useWatch instead of getValues-on-mount.
  const watchedAddressType = useWatch({ control, name: `schedule.${isPickupOrDelivery}_address_type` });
  const watchedAirportCode = useWatch({ control, name: `schedule.${isPickupOrDelivery}_airport_code` });
  const watchedAirportName = useWatch({ control, name: `schedule.${isPickupOrDelivery}_airport` });
  const watchedSeaportCode = useWatch({ control, name: `schedule.${isPickupOrDelivery}_seaport_code` });
  const watchedSeaportName = useWatch({ control, name: `schedule.${isPickupOrDelivery}_seaport` });
  const lastHydratedQueryRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOptionalPortAddressFormEnabled || !enable_new_address_structure) return;
    if (watchedAddressType !== "airport" && watchedAddressType !== "seaport") return;
    const code = watchedAddressType === "airport" ? watchedAirportCode : watchedSeaportCode;
    const portName = watchedAddressType === "airport" ? watchedAirportName : watchedSeaportName;
    const query = code || portName;
    if (!query) return;
    if (lastHydratedQueryRef.current === query) return;
    lastHydratedQueryRef.current = query;
    const endpoint = watchedAddressType === "airport" ? "/common/airport" : "/common/seaport";
    getAdressTypeOption(endpoint, query)
      .then((res) => {
        const first = res?.data?.results?.[0];
        if (!first) return;
        const codeType = isPickupOrDelivery === "stop1" ? "destination" : isPickupOrDelivery;
        const hasDraftSelectedAddress =
          Boolean(getValues(`schedule.${codeType}_address_reference_number`)) ||
          Boolean(getValues(`schedule.${codeType}_address_pk`));
        handleFillPortAddressOption([
          {
            id: first.id,
            label: first.name,
            value: first.name,
            city_name: first.city_name,
            country_name: first.country_name,
            country_code: first.country_code,
            code: first.un_locode || first.code,
            address: first.address,
          },
        ], { preserveSelectedAddress: hasDraftSelectedAddress });
      })
      .catch(() => {
        // best-effort hydration; allow retry on next value change
        lastHydratedQueryRef.current = null;
      });
  }, [
    isOptionalPortAddressFormEnabled,
    enable_new_address_structure,
    isPickupOrDelivery,
    watchedAddressType,
    watchedAirportCode,
    watchedAirportName,
    watchedSeaportCode,
    watchedSeaportName,
    handleFillPortAddressOption,
  ]);

  return (
    <div className="w-192">
      <SearchSelect
        preselect={false}
        testId={
          isPickupOrDelivery === "pickup"
            ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_PORT_SELECT
            : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_PORT_SELECT
        }
        name={
          getValues(`schedule.${isPickupOrDelivery}_address_type`) !== "address" &&
          getValues(`schedule.${isPickupOrDelivery}_address_type`) === "airport"
            ? `schedule.${isPickupOrDelivery}_airport`
            : `schedule.${isPickupOrDelivery}_seaport`
        }
        required={
          getValues(`schedule.${isPickupOrDelivery}_address_type`) === "airport"
            ? "Please select a valid airport"
            : "Please select a valid port"
        }
        disable={
          getValues(`schedule.${isPickupOrDelivery}_address_type`) !== "address" &&
          getValues(`schedule.${isPickupOrDelivery}_address_type`) === "airport"
            ? formState.disabledFields[`schedule.${isPickupOrDelivery}_airport`]
            : formState.disabledFields[`schedule.${isPickupOrDelivery}_seaport`]
        }
        options={portAddressOption.map((option: IAddressOption) => ({
          id: option.id,
          label: option.name,
          value: option.name,
          city_name: option.city_name,
          country_name: option.country_name,
          country_code: option.country_code,
          code: option.un_locode || option.code,
          address: option.address,
        }))}
        label={
          !enable_new_address_structure
            ? ""
            : getValues(`schedule.${isPickupOrDelivery}_address_type`) === "airport"
            ? intl.formatMessage({
                id: "new-request.schedule.airport",
                defaultMessage: "Search airport",
              })
            : intl.formatMessage({
                id: "new-request.schedule.port",
                defaultMessage: "Search port",
              })
        }
        placeholder={
          getValues(`schedule.${isPickupOrDelivery}_address_type`) === "airport"
            ? intl.formatMessage({
                id: "new-request.schedule.autoCompletePlaceholder.searchAirport",
                defaultMessage: "Search airport",
              })
            : intl.formatMessage({
                id: "new-request.schedule.autoCompletePlaceholder.searchPort",
                defaultMessage: "Search port",
              })
        }
        filterBy={["label", "city_name", "country_name", "code"]}
        handleRenderOption={handleRenderAddressOption}
        onInputChange={(val: string) => {
          if (!val?.trim()) {
            clearSelectedPort();
            clearSelectedAddress();
          }
          setSearch(val);
        }}
        onSelect={handleFillPortAddressOption}
      />
    </div>
  );
}
